/**
 * Textos de `workspace` en español — la fuente. `en/workspace.ts` tiene que tener
 * exactamente las mismas claves (lo exige el tipo), así que una clave nueva
 * acá rompe el typecheck hasta que alguien la traduce.
 *
 * Interpolación con funciones, no con plantillas "{n}": el compilador revisa
 * los argumentos y cada idioma arma su propia gramática (plurales, orden).
 */
const workspace = {
  /** El panel (`JobApplyDashboardPage`). */
  dashboard: {
    loading: 'Cargando tu panel',
    title: 'Panel',
    reconnectGmail: 'Reconectar Gmail',
    changeCv: 'Cambiar CV',
    /** El banner de la app móvil (`AppDownloadBanner`). */
    appBanner: {
      title: 'TBit Jobs ya está en tu celular',
      lede: 'Entérate al instante cuando una empresa te responde y contesta desde donde estés.',
      appStore: 'Descargar en App Store',
      playStore: 'Descargar en Google Play',
      playStoreSoon: 'Próximamente en Google Play',
      soon: 'Próximamente',
      dismiss: 'Cerrar',
    },
    /**
     * `GmailReconnectNotice`: Google rechazó el token (revocado, vencido o sin
     * permisos) y no sale ninguna postulación hasta que la persona reconecte.
     */
    gmailDead: {
      title: 'Tu Gmail se desconectó',
      body: 'Google ya no nos deja enviar desde tu cuenta, así que tus postulaciones están en pausa. Reconéctalo y volvemos a postular por ti.',
    },
    /** El aviso de una línea cuando la hoja de vida va por debajo del umbral. */
    scoreNudge: 'Tu hoja de vida puede mejorar — mira cómo',
    sender: {
      /** Va seguido de la dirección en negrita. */
      from: 'Tus postulaciones salen de',
      preparing: 'Estamos preparando la direccion desde la que saldran tus postulaciones.',
      /** "…te reenviamos el correo a <dirección>." */
      forwardBefore: 'Cuando una empresa responda, te reenviamos el correo a ',
      forwardAfter: '.',
      forwardOff:
        'El reenvio de respuestas a tu correo esta desactivado. Escribenos si quieres volver a activarlo.',
      noInbox: 'No necesitas revisar otra bandeja de entrada ni conectar tu cuenta de Google.',
    },
    /** El regreso de Stripe (`?checkout=success|cancel`). */
    checkout: {
      pending: 'Confirmando tu pago... un momento.',
      slow: 'Recibimos tu suscripción. Si tu plan todavía no aparece activo, recarga la página en un minuto — o escríbenos y lo revisamos contigo.',
      /** "<strong>Tu plan está activo.</strong> Desde hoy…" */
      activeStrong: 'Tu plan está activo.',
      activeAfter:
        ' Desde hoy postulamos por ti todos los días hábiles y las respuestas te llegan a tu correo.',
    },
    /** La línea del plan activo: "<strong>Plan…</strong> activo — se renueva el … · …". */
    plan: {
      activeAfter: ' activo',
      renews: (date: string) => ` — se renueva el ${date}`,
      dailyLimit: (n: number) => `Postulamos por ti hasta ${n} veces cada día hábil`,
      /** En la prueba gratis: "<strong>Prueba gratis hasta el …</strong>. Al terminar, … · …". */
      trialUntil: (date: string) => `Prueba gratis hasta el ${date}`,
      trialActive: 'Prueba gratis activa',
      trialThen: (price: string) => `. Al terminar, se cobran ${price} al mes`,
      /** Enlace a Perfil → Plan (no al portal directo): ahí se ve todo antes de decidir. */
      trialManage: 'Administrar o cancelar',
    },
    /** El plan existió y ya no vale. */
    expired: {
      headline: (date: string) => `Tu plan venció el ${date}`,
      headlineNoDate: 'Tu plan ya no está vigente',
      lede: 'Reactívalo y volvemos a postular por ti todos los días hábiles.',
    },
    generatingDrafts: 'Preparando tus primeras postulaciones...',
    weekend:
      'Fin de semana — las postulaciones se reanudan el lunes. Los correos enviados en fin de semana rara vez son revisados.',
    drafts: {
      titleActive: 'Tus postulaciones están listas',
      titleDemo: 'Mira cómo llega tu postulación',
      noteActive: 'Estas salen a los empleadores en el próximo envío. No tienes que hacer nada.',
      noteDemo:
        'Envíate cada una a tu correo y verás exactamente lo que recibe un empleador: el asunto, el mensaje y tu hoja de vida en PDF.',
      startSending: 'Empezar a enviar a los empleadores',
      startNote: 'A partir de ahí seguimos solos: postulamos por ti cada día hábil.',
      activationFailed: 'No pudimos activar el envío. Inténtalo de nuevo en un momento.',
      activationConnection: 'Revisa tu conexión e inténtalo de nuevo.',
    },
    /** Una postulación lista con su botón (`DraftDemoCard`). */
    draftCard: {
      sent: 'Enviada a tu correo — revísala',
      send: 'Enviar a mi correo',
      ready: 'Lista para salir',
    },
    /**
     * El panel sin muro (`free_tier` del servidor). La app nunca dice «gratis»
     * de quien la usa: la persona está en TBit Jobs y el plan es algo que se suma.
     */
    freeTier: {
      /** La cinta de `PlanNudge`, neutra: dice qué hace el plan, no lo que falta. */
      ribbon: 'Con el plan, TBit elige las vacantes que encajan contigo y postula por ti cada día hábil.',
      /** El aviso que abre el botón de la cinta. */
      headline: 'Tú postulas. Con el plan, postulamos por ti.',
      lede: 'TBit postula por ti todos los días hábiles, con una carta para cada empleador escrita con tu hoja de vida.',
      /** «Postulaciones recientes» vacía, cuando ya no hay muestras. */
      recentEmptyNote: 'Postula a una vacante de la lista y la ves aquí.',
    },
    /** «Vacantes para ti» (`JobsForYouCard`): las del feed, con su botón. */
    jobsForYou: {
      title: 'Vacantes para ti',
      quota: (used: number, limit: number) => `${used} de ${limit} hoy`,
      /** «empieza 15 may». */
      starts: (date: string) => `empieza ${date}`,
      applyGmail: 'Postular con mi Gmail',
      applyCopy: 'Postular desde mi correo',
      closed: 'Este empleador ya cerró esta vacante.',
      applied: 'Postulaste hoy',
      footer: 'Las elegimos por tu hoja de vida, tu oficio y tus países.',
      /**
       * Sin plan (2026-10-30): la recomendación va borrosa. Las vacantes no: están
       * todas en el buscador de abajo. Nunca «plan gratis» ni «desbloquea vacantes».
       */
      lockedLede: (n: number) =>
        n === 1
          ? 'Encontramos 1 vacante que encaja con tu hoja de vida. Con el plan te decimos cuál es y postulamos por ti.'
          : `Encontramos ${n} vacantes que encajan con tu hoja de vida. Con el plan te decimos cuáles son y postulamos por ti.`,
      lockedSearchHint: 'Buscando tú, postulas una por una desde tu correo. Con el plan las elegimos y postulamos por ti.',
      lockedSearchSelf: 'Prefiero buscar por mi cuenta',
      lockedCta: 'Ver mis recomendadas',
      lockedRow: 'Vacante recomendada',
      appliedTodayTitle: (n: number) =>
        n === 1 ? 'Hoy postulamos por ti a 1 vacante' : `Hoy postulamos por ti a ${n} vacantes`,
      appliedTodayChip: 'Enviada',
      recommendedTitle: 'Recomendadas para ti',
      saved: {
        title: 'Guardadas',
        lede: 'Las vacantes que guardaste para ver después.',
        save: 'Guardar vacante',
        unsave: 'Quitar de guardadas',
        failed: 'No pudimos guardar el cambio. Intenta de nuevo.',
        tooMany: 'Ya tienes 200 vacantes guardadas. Quita alguna para guardar otra.',
      },
      search: {
        title: 'Buscar vacantes',
        lede: 'Todas las vacantes de tu oficio y tus países, con o sin plan.',
        placeholder: 'Oficio, ciudad o empresa. Por ejemplo: soldador en Texas',
        submit: 'Buscar',
        newest: 'Las más nuevas',
        mix: 'Para empezar a buscar',
        pay: 'Mejor pago por hora',
        soon: 'Empiezan pronto',
        filters: {
          program: 'Programa',
          anyProgram: 'Todos los programas',
          programs: { h2b: 'H-2B', h2a: 'H-2A', jobbank: 'Canadá', eures: 'Europa' },
          region: 'Lugar',
          anyRegion: 'Cualquier lugar',
          wage: 'Pago por hora',
          anyWage: 'Cualquier pago',
          wageAtLeast: (n: number) => `Desde $${n} la hora`,
          start: 'Empieza',
          anyStart: 'Cuando sea',
          starts: { month: 'En el próximo mes', quarter: 'En 1 a 3 meses', later: 'En más de 3 meses' },
          housing: 'Con alojamiento',
          sort: 'Orden',
          sorts: { mix: 'Mezcla', newest: 'Más nuevas', pay: 'Mejor pago por hora', soon: 'Empiezan pronto' },
          clear: 'Quitar filtros',
        },
        results: (q: string) => `Resultados para «${q}»`,
        more: 'Ver más',
        empty: 'No encontramos vacantes con eso. Prueba con otras palabras.',
        failed: 'No pudimos buscar. Intenta de nuevo en un momento.',
        loading: 'Buscando',
        alreadyApplied: 'Ya postulaste',
      },
      empty: 'Todavía no hay vacantes para tu perfil. Cuando lleguen, salen aquí.',
      failed: 'No pudimos cargar las vacantes. Recarga la página en un momento.',
      loading: 'Buscando vacantes para ti',
      jobBank: 'Job Bank con LMIA',
    },
    /** La hoja de postular a mano (`ManualApplySheet`). */
    manualSheet: {
      label: 'Tu postulación',
      template: 'Plantilla',
      noteGmail: 'Sale desde tu Gmail con tu hoja de vida en PDF.',
      noteCopy: 'La envías tú desde tu correo. Adjunta tu hoja de vida en PDF.',
      loading: 'Preparando tu postulación',
      to: 'Para:',
      subject: 'Asunto:',
      planTitle: 'La carta del plan, para esta vacante',
      planCompany: (company: string, date: string) =>
        date ? `Nombra a ${company} y el inicio del ${date}.` : `Nombra a ${company}.`,
      planYears: (years: number | null) =>
        years
          ? `Cuenta tus ${years} ${years === 1 ? 'año' : 'años'} de experiencia con lo que dice tu hoja de vida.`
          : 'Cuenta tu experiencia con lo que dice tu hoja de vida.',
      planAuto: 'Sale sola, sin que tengas que entrar.',
      quotaLine: (count: number, limit: number) => `Esta postulación cuenta en tu cupo de hoy: ${count} de ${limit}.`,
      quotaShared: 'Las que TBit envía por ti y las tuyas salen del mismo cupo.',
      sendGmail: 'Enviar desde mi Gmail',
      sendCopy: 'Copiar y abrir mi correo',
      sent: 'Enviada. Si el empleador responde, te llega a tu Gmail.',
      copied: 'Copiamos el texto. Pégalo en un correo nuevo para esta dirección y adjunta tu hoja de vida:',
      copyFailed: 'No pudimos copiarlo solos. Selecciona el texto y cópialo, y envíalo a esta dirección:',
      openMail: 'Abrir mi correo',
      dailyLimit: (limit: number) => `Llegaste a tus ${limit} de hoy. Mañana sigues.`,
      noEmail: 'Esta vacante no trae un correo para postular.',
      notActive: 'Esta vacante ya no está activa.',
      paused: 'Tus envíos están en pausa. Escríbenos y lo revisamos contigo.',
      termsRequired: 'Antes de postular, acepta los términos de TBit Jobs.',
      termsOpen: 'Aceptar los términos',
      alreadyApplied: 'Ya postulaste a esta vacante.',
      reconnect: 'Google pide que vuelvas a conectar tu Gmail para enviar desde él.',
      cvRequired: 'Sube tu hoja de vida para postular.',
      cvLink: 'Subir mi hoja de vida',
      failed: 'No pudimos enviarla. Inténtalo de nuevo en un momento.',
      previewFailed: 'No pudimos preparar la postulación. Inténtalo de nuevo.',
      close: 'Cerrar',
    },
    /** «Lo que se te fue» (`MissedWeekCard`): conteos de la semana, sin modelo. */
    missedWeek: {
      label: 'Lo que se te fue esta semana',
      title: (postings: number, applied: number) =>
        `${postings === 1 ? 'Llegó 1 vacante' : `Llegaron ${postings} vacantes`} para tu perfil esta semana. Postulaste a ${applied}.`,
      withPlan: 'Con el plan salen solas, cada una con una carta para ese empleador.',
      replies: (n: number) => (n === 1 ? '1 empleador te contestó.' : `${n} empleadores te contestaron.`),
      repliesAfter: 'Con el plan, TBit te dice cuáles son una oportunidad y qué contestarles en inglés.',
    },
    /**
     * `ApplyCountriesCard`: a qué países se envían las postulaciones. Es la
     * única preferencia del candidato que mueve el envío, por eso vive en el
     * panel y no en Perfil → Preferencias (que es para las agencias).
     */
    countries: {
      title: 'Dónde postulamos por ti',
      lede: 'Elige uno o varios. Esto cambia a qué vacantes enviamos tus postulaciones.',
      groupLabel: 'Países donde postulamos',
      options: {
        US: 'Estados Unidos',
        CA: 'Canadá',
        EU: 'Europa',
      },
      caNote:
        'Canadá: vacantes de Job Bank con LMIA que aceptan candidatos del exterior. El permiso de trabajo sale de esa LMIA.',
      euNote:
        'Europa: vacantes de EURES. Sin papeles de la UE te postulamos a las que dicen aceptar candidatos de fuera de la UE y a oficios con permiso por escasez en España (electricistas, carpintería de aluminio, metálica o PVC, operadores de grúa y marina mercante), aunque no tengas papeles.',
      lastOne: 'Siempre queda al menos un país marcado.',
      nextBatch: 'El cambio aplica a las próximas postulaciones. Las que ya están listas no cambian.',
      save: 'Guardar países',
      /** Recibe los nombres ya unidos con `join`. */
      saved: (names: string) => `Listo. Desde ahora postulamos en ${names}.`,
      join: (names: string[]) =>
        names.length > 1 ? `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}` : (names[0] ?? ''),
      saveFailed: 'No pudimos guardar los países. Intenta de nuevo.',
      connectionError: 'Error de conexión. Intenta de nuevo.',
    },
    /** Cuando la demostración no salió (`lib/demo.demoErrorCopy`). */
    demoErrors: {
      unavailable:
        'No pudimos enviarla en este momento. Tu postulación sigue lista — vuelve a intentar en unos minutos.',
      throttled: (minutes: number) =>
        `Vas muy rápido. Puedes enviar otra en ${minutes} minuto${minutes === 1 ? '' : 's'}.`,
      failed: 'Algo se interpuso al enviarla. Tu postulación sigue lista — inténtalo de nuevo.',
    },
    stats: {
      today: 'Hoy',
      remaining: (n: number) => `${n} restantes`,
      pending: 'Pendientes',
      replied: 'Respuestas',
      interviews: 'Entrevistas',
      ready: 'Listas',
    },
    recent: {
      title: 'Postulaciones recientes',
      emptyTitle: 'Aun no hay postulaciones enviadas',
      emptyNoteAuto: 'El sistema enviara postulaciones automaticamente en el proximo ciclo.',
      emptyNoteDemo: 'Envíate una postulación a tu correo para ver cómo le llega a un empleador.',
      subject: 'Asunto',
      status: 'Estado',
      date: 'Fecha',
    },
  },

  /** Tus postulaciones (`JobApplyHistoryPage`). */
  history: {
    title: 'Tus postulaciones',
    filterLabel: 'Filtrar por estado',
    /** Etiquetas de estado en la tabla. */
    status: {
      SENT: 'Enviada',
      REPLIED: 'Con respuesta',
      INTERVIEW: 'Entrevista',
      REJECTED: 'Rechazada',
      QUEUED: 'En cola',
      NO_RESPONSE: 'Sin respuesta',
    } as Record<string, string>,
    /** Opciones del filtro, por el `value` que se envía ('' = todos). */
    filter: {
      all: 'Todos los estados',
      SENT: 'Enviadas',
      REPLIED: 'Con respuesta',
      INTERVIEW: 'Entrevista',
      REJECTED: 'Rechazadas',
      QUEUED: 'En cola',
      NO_RESPONSE: 'Sin respuesta',
    },
    httpError: (status: number) => `Error ${status}`,
    loadError: 'Error al cargar postulaciones.',
    emptyFilteredTitle: 'Nada con este estado',
    emptyFilteredNote: 'No hay postulaciones con este estado.',
    emptyTitle: 'Aun no se han enviado postulaciones',
    emptyNote:
      'Una vez configurada tu hoja de vida y preferencias, las postulaciones apareceran aqui.',
    company: 'Empresa',
    position: 'Posicion',
    statusHeader: 'Estado',
    date: 'Fecha',
    total: (n: number) => `${n} postulacion${n !== 1 ? 'es' : ''} en total`,
    previous: 'Anterior',
    next: 'Siguiente',
  },

  /** El inbox (`JobApplyMessagesPage`). */
  inbox: {
    title: 'Mensajes',
    lede: 'Las respuestas de los empleadores y las invitaciones de las agencias, en un solo lugar.',
    loadError: 'No pudimos cargar tus mensajes. Recarga la página.',
    loadMoreError: 'No pudimos traer más conversaciones. Intenta de nuevo.',
    filterLabel: 'Filtrar mensajes',
    filters: {
      all: 'Todos',
      unread: 'No leídos',
      invitations: 'Invitaciones',
    },
    loading: 'Cargando tus mensajes',
    /** Cada vacío dice qué falta y, cuando la hay, dónde está la puerta. */
    empty: {
      all: {
        title: 'Todavía no hay conversaciones',
        note: 'Cuando un empleador responda a una de tus postulaciones, lo verás aquí.',
      },
      unread: {
        title: 'Estás al día',
        note: 'No tienes respuestas ni invitaciones sin abrir.',
      },
      invitations: {
        title: 'Todavía no tienes invitaciones',
        note: 'Cuando una agencia quiera hablar contigo te avisamos aquí y por correo.',
      },
    },
    seeApplications: 'Ver mis postulaciones',
    activateTalentPool: 'Activar la bolsa de talento',
    loadMore: 'Ver conversaciones anteriores',
    /** Aviso cuando la lista no pudo leer Gmail porque el permiso se venció. */
    gmailReconnect: 'Para ver aquí las respuestas que te llegan a Gmail, reconecta tu cuenta.',
    reconnectGmail: 'Reconectar Gmail',
  },

  /** El detalle de un hilo (`JobApplyThreadPage`). */
  thread: {
    loading: 'Abriendo la conversación',
    loadError: 'No pudimos cargar esta conversación. Recarga la página.',
    scope: 'Mensajes',
    fallbackTitle: 'Conversación',
    notFoundTitle: 'No encontramos esta conversación',
    notFoundNote: 'Puede que el enlace sea de otra cuenta, o que la postulación ya no exista.',
    seeMessages: 'Ver mis mensajes',
    back: 'Volver',
    opportunity: 'Oportunidad',
    appliedOn: (date: string) => `Te postulaste el ${date}`,
    wroteTo: (email: string) => ` · le escribimos a ${email}`,
    reply: 'Responder',
    gmailTitle: 'Esta conversación vive en tu Gmail',
    gmailNote:
      'Tus postulaciones salen desde tu propia cuenta de Google, así que las respuestas llegan a tu bandeja de entrada. Ábrela en Gmail para contestarle a la empresa: así la empresa te ve a ti y no a un intermediario.',
    openInGmail: 'Abrir en Gmail',
    /** Debajo del compositor en un hilo de Gmail: de dónde sale la respuesta. */
    gmailReplyNote: (address: string) =>
      address
        ? `Esta conversación es con tu Gmail: tu respuesta sale desde tu correo (${address}).`
        : 'Esta conversación es con tu Gmail: tu respuesta sale desde tu correo.',
    gmailReconnectTitle: 'Reconecta tu Gmail',
    gmailReconnectNote:
      'Google ya no nos deja leer esta conversación. Reconecta tu cuenta para verla y responder desde aquí; mientras tanto puedes abrirla en Gmail.',
    reconnectGmail: 'Reconectar Gmail',
    gmailUnavailableTitle: 'No pudimos leer tu Gmail ahora',
    gmailUnavailableNote: 'Intenta de nuevo en unos minutos, o abre la conversación en Gmail.',
    retry: 'Intentar de nuevo',
    /** Estado de la postulación en el encabezado del hilo. */
    status: {
      SENT: 'Enviada',
      REPLIED: 'Respondieron',
      INTERVIEW: 'Entrevista',
      REJECTED: 'Rechazada',
      NO_RESPONSE: 'Sin respuesta',
    } as Record<string, string>,
  },

  /** Lo que se dice igual de una invitación en la fila del inbox y en su pantalla. */
  invitation: {
    accepted: 'Aceptaste',
    declined: 'No aceptaste',
    notShared: 'No compartimos tus datos con esta agencia.',
  },

  /** Invitaciones de agencia (`JobApplyInvitationsPage`). */
  invitations: {
    loadError: 'No pudimos cargar tus invitaciones. Recarga la página.',
    connection: 'Error de conexión. Intenta de nuevo.',
    alreadyAnswered: 'Esta invitación ya fue respondida.',
    respondFailed: 'No pudimos registrar tu respuesta.',
    acceptedNotice: (agency: string) =>
      `Le compartimos tus datos a ${agency}. Te escribirán directamente.`,
    declinedNotice: 'Listo, no compartimos nada. La agencia solo sabe que no te interesa.',
    loading: 'Cargando tus invitaciones',
    focusedTitle: 'Una agencia quiere contactarte',
    title: 'Invitaciones',
    seeAll: 'Ver todas',
    notFoundTitle: 'No encontramos esta invitación',
    notFoundNote: 'Puede que el enlace sea de otra cuenta, o que la agencia la haya retirado.',
    seeMine: 'Ver mis invitaciones',
    emptyTitle: 'Todavía no tienes invitaciones',
    emptyNote:
      'Cuando una agencia quiera hablar contigo te avisamos aquí y por correo. Para que te encuentren, activa la bolsa de talento en tu perfil.',
    activateTalentPool: 'Activar la bolsa de talento',
    noPending: 'No tienes invitaciones pendientes. Abajo está el historial.',
    /** Va seguido del nombre de la agencia. */
    agency: 'Agencia:',
    invitedOn: (date: string) => `Te invitaron el ${date}`,
    pendingNote:
      'Vieron tu perfil sin tus datos personales y quieren hablar contigo. Si aceptas, les damos tu nombre y tu correo para que te escriban. Si no, no compartimos nada.',
    decline: 'No, gracias',
    accept: 'Sí, que me contacten',
    sharedOn: (agency: string, date: string) => `Compartimos tus datos con ${agency} el ${date}.`,
  },

  /** Fila de hilo en el inbox (`ThreadListItem`). */
  threadItem: {
    you: 'Tú: ',
    unread: (n: number) => `${n} sin leer`,
    replied: 'Respondieron',
    /** lib/replyClassifier (servidor) dijo que la respuesta de la empresa es una oportunidad. */
    opportunity: 'Oportunidad',
  },

  /** Fila de invitación en el inbox (`InvitationListItem`). */
  invitationItem: {
    label: 'Invitación',
    pendingPreview:
      'Una agencia vio tu perfil sin tus datos y quiere contactarte. Tú decides si les damos tu correo.',
    sharedWith: (agency: string) => `Compartimos tus datos con ${agency}.`,
    unanswered: 'Sin responder',
  },

  /** "Mensajes recientes" en el panel (`RecentMessagesCard`). */
  recentMessages: {
    scope: 'Bandeja',
    title: 'Mensajes recientes',
    seeAll: 'Ver todos',
    emptyTitle: 'Todavía no hay conversaciones',
    emptyNote: 'Cuando un empleador responda, lo verás aquí.',
  },

  /** El compositor de respuesta (`ReplyComposer`). */
  composer: {
    label: 'Escribe tu respuesta',
    placeholder: 'Escribe tu respuesta para la empresa…',
    retry: 'Reintentar',
    tooLong: (n: number) => `Te pasaste por ${n} caracteres.`,
    to: (email: string) => `Le llega a ${email}`,
    shortcut: 'Ctrl + Enter para enviar',
    sending: 'Enviando…',
    send: 'Enviar respuesta',
  },

  /**
   * La respuesta sugerida (`SuggestedReplyCard`). Los borradores vienen del
   * servidor en el idioma de la EMPRESA; esto es sólo el marco, en el de la
   * persona.
   */
  suggestion: {
    scope: 'Respuesta sugerida',
    loading: 'Leyendo lo que te escribió la empresa…',
    /** Qué tipo de respuesta mandó la empresa, en una línea. */
    typology: {
      interest: 'Les interesa tu perfil',
      interview: 'Quieren hablar contigo',
      info_request: 'Te piden más datos',
      locals_only: 'Por ahora solo contratan en EE.UU.',
      rejection: 'No siguieron con tu postulación',
      other: 'Otro tipo de respuesta',
    } as Record<string, string>,
    /** Las opciones de respuesta (`REPLY_OPTIONS` del servidor), por id. */
    optionsTitle: '¿Qué quieres contestar?',
    optionsNote: 'Toca una y te escribimos borradores para eso.',
    replyOptions: {
      interview_confirm: 'Confirmar disponibilidad',
      interview_reschedule: 'Proponer otro horario',
      interview_details: 'Pedir detalles de la entrevista',
      info_send: 'Enviar lo que piden',
      info_clarify: 'Preguntar qué documentos exactamente',
      interest_confirm: 'Confirmar interés y disponibilidad',
      interest_question: 'Hacer una pregunta sobre el puesto',
      locals_visa_next_season: 'Aclarar que busco la visa y preguntar por la próxima convocatoria',
      locals_keep_on_file: 'Pedir que me tengan en cuenta',
      locals_transfer: 'Decir que estoy en EE. UU. con H-2 y disponible para transferencia',
      rejection_future: 'Agradecer y pedir que me tengan en cuenta para otra temporada',
    } as Record<string, string>,
    optionLoading: 'Escribiendo borradores para esa opción…',
    draftsTitle: 'Puedes contestar así',
    languageNote: 'Los borradores van en el idioma en que te escribió la empresa.',
    option: (n: number) => `Opción ${n}`,
    use: 'Usar esta respuesta',
    used: 'Ya está en tu respuesta. Revísala antes de enviar.',
    note: 'Nada se envía solo: el texto queda en tu respuesta para que lo revises y lo cambies como quieras.',
    regenerate: 'Ver otras opciones',
    retry: 'Intentar de nuevo',
    errors: {
      failed: 'No pudimos preparar una sugerencia ahora. Puedes escribir tu respuesta o intentar de nuevo.',
      rateLimited: (minutes: number) =>
        `Pediste varias sugerencias seguidas. Intenta de nuevo en ${minutes} minutos.`,
      network: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.',
    },
  },

  /** Un mensaje del hilo (`MessageBubble`). */
  bubble: {
    application: 'Postulación enviada',
    you: 'Tú',
    company: 'La empresa',
    new: 'Nuevo',
    sendingLabel: 'Enviando',
    cvAttached: 'Tu hoja de vida iba adjunta',
    sending: 'Enviando…',
  },

  /** Timestamps relativos del inbox (`lib/threads.relativeTime`). */
  time: {
    now: 'ahora',
    minutes: (n: number) => `hace ${n} min`,
    hours: (n: number) => `hace ${n} h`,
    yesterday: 'ayer',
    days: (n: number) => `hace ${n} días`,
  },

  /** La frase por cada `code` de error al responder (`lib/threads.replyErrorMessage`). */
  replyErrors: {
    network: 'No pudimos enviar tu respuesta. Revisa tu conexión e intenta de nuevo.',
    gmailChannel: 'Este hilo vive en tu Gmail. Ábrelo allí para responderle a la empresa.',
    rateLimited: (minutes: number) =>
      `Enviaste varias respuestas seguidas. Vuelve a intentar en ${minutes} minutos.`,
    bounced: 'El correo de la empresa rebotó: no existe o no acepta mensajes. Tu texto sigue aquí.',
    sendFailed: 'No pudimos entregar tu respuesta. Tu texto sigue aquí, intenta de nuevo.',
    threadNotSent: 'Esta postulación todavía no ha salido, así que aún no hay conversación.',
    noContactEmail: 'Esta oferta no dejó un correo de contacto, así que no hay a dónde responder.',
    senderNotProvisioned:
      'Todavía estamos preparando tu dirección de envío. Intenta de nuevo en unos minutos.',
    notFound: 'Este hilo ya no está disponible.',
    gmailReconnect: 'Reconecta tu Gmail para responder desde aquí. Tu texto sigue aquí.',
    gmailUnavailable: 'Gmail no respondió. Tu texto sigue aquí, intenta de nuevo.',
    gmailThreadMissing: 'Esta conversación ya no está en tu Gmail, así que no hay hilo donde responder.',
    recipientChanged:
      'Cambió a quién le llega tu respuesta. Revisa el destinatario de abajo y vuelve a enviar: tu texto sigue aquí.',
    replyInProgress: 'Tu respuesta ya se está enviando. Espera un momento antes de intentar de nuevo.',
    generic: 'No pudimos enviar tu respuesta. Intenta de nuevo.',
  },
};

export default workspace;
